import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import { localDateOf, localWallTimeUtc } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { addDaysISO } from '../../lib/format';
import { useOrgClock } from '../../lib/useOrgClock';
import { weekDays, weekStart } from '../schedule/weekGrid';
import { layoutLane } from '../schedule/timeline';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { UnderlineTabs, UnderlineTabStrip, UnderlineTabPanel } from '../ui/Tabs';
import { Field, field } from '../create/Field';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconTeam } from '../icons';

type Timeline = inferOutput<
  TRPCOptionsProxy<AppRouter>['scheduleAvailability']['timeline']
>;
const instant = (value: string | Date) => new Date(value).getTime();
const clockTime = (value: string | Date, timezone: string) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
const intervalText = (start: string | Date, end: string | Date, timezone: string) =>
  `${clockTime(start, timezone)} – ${clockTime(end, timezone)}`;

/** The bars are geometry only. Working/free interval authority stays on the server. */
export function CrewTimelineRows({
  crew,
  days,
  timezone,
}: {
  crew: Timeline['crew'];
  days: string[];
  timezone: string;
}) {
  const windows = days.map((day) => {
    const start = localWallTimeUtc(day, 0, timezone).getTime();
    const end = localWallTimeUtc(addDaysISO(day, 1), 0, timezone).getTime();
    return { day, start, end, duration: (end - start) / 60_000 };
  });
  return (
    <div className="space-y-6">
      {crew.map((person) => (
        <section
          key={person.userId}
          className="space-y-3"
          aria-label={`${person.name} calendar`}
        >
          <h4 className="text-sm font-semibold">{person.name}</h4>
          {!person.hoursKnown ? (
            <Notice tone="warn">
              Working hours unknown. Empty calendar space does not mean this crew is
              available.
            </Notice>
          ) : null}
          <div className={days.length > 1 ? 'grid gap-3 xl:grid-cols-2' : 'space-y-3'}>
            {windows.map(({ day, start, end, duration }) => {
              const overlaps = (a: string | Date, b: string | Date) =>
                instant(a) < end && instant(b) > start;
              const hours = person.workingIntervals.filter((span) =>
                overlaps(span.startsAt, span.endsAt),
              );
              const off = person.timeOff.filter((span) =>
                overlaps(span.startsAt, span.endsAt),
              );
              const visits = person.commitments.filter((span) =>
                overlaps(span.visibleStartsAt, span.visibleEndsAt),
              );
              const geometry = (a: string | Date, b: string | Date) => ({
                start: Math.max(0, (instant(a) - start) / 60_000),
                end: Math.min(duration, (instant(b) - start) / 60_000),
              });
              const positioned = layoutLane(
                visits.map((visit) => ({
                  ...geometry(visit.visibleStartsAt, visit.visibleEndsAt),
                  visit,
                })),
                { start: 0, end: duration },
              );
              return (
                <article
                  key={day}
                  className="min-w-0 rounded-[var(--radius)] border border-border p-3"
                >
                  <h5 className="text-xs font-semibold">
                    {day} · {duration / 60}-hour day
                  </h5>
                  <div aria-hidden="true" className="mt-3 space-y-1">
                    <div className="relative h-3 overflow-hidden rounded bg-surface-2">
                      {hours.map((span, index) => {
                        const bar = geometry(span.startsAt, span.endsAt);
                        return (
                          <span
                            key={index}
                            className="absolute inset-y-0 bg-success/40"
                            style={{
                              left: `${(bar.start / duration) * 100}%`,
                              width: `${((bar.end - bar.start) / duration) * 100}%`,
                            }}
                          />
                        );
                      })}
                    </div>
                    <div className="relative h-12 rounded bg-surface-2">
                      {positioned.map(({ item, left, width, track, tracks }) => (
                        <span
                          key={item.visit.visitId}
                          className={`absolute overflow-hidden rounded border px-1 text-[10px] leading-tight ${item.visit.uncertain ? 'border-dashed border-warn bg-warn/20' : 'border-info/50 bg-info/15'}`}
                          style={{
                            left: `${left * 100}%`,
                            width: `${width * 100}%`,
                            top: `${(track / tracks) * 100}%`,
                            height: `${100 / tracks}%`,
                          }}
                          title={item.visit.title}
                        >
                          {item.visit.title}
                        </span>
                      ))}
                    </div>
                    <div className="relative h-3 overflow-hidden rounded bg-surface-2">
                      {off.map((span, index) => {
                        const bar = geometry(span.startsAt, span.endsAt);
                        return (
                          <span
                            key={index}
                            className="absolute inset-y-0 bg-danger/40"
                            style={{
                              left: `${(bar.start / duration) * 100}%`,
                              width: `${((bar.end - bar.start) / duration) * 100}%`,
                            }}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>{clockTime(new Date(start), timezone)}</span>
                      <span>{clockTime(new Date((start + end) / 2), timezone)}</span>
                      <span>{clockTime(new Date(end), timezone)}</span>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-2 text-xs">
                    <li>
                      <span className="font-medium">Working hours: </span>
                      {person.hoursKnown
                        ? hours.length
                          ? hours
                              .map((span) =>
                                intervalText(span.startsAt, span.endsAt, timezone),
                              )
                              .join('; ')
                          : 'None in this day'
                        : 'Unknown'}
                    </li>
                    {visits.map((visit) => (
                      <li key={visit.visitId}>
                        <Link
                          className="font-medium underline underline-offset-2"
                          to="/work-orders/$workOrderId"
                          params={{ workOrderId: visit.workOrderId }}
                        >
                          {visit.title}
                        </Link>{' '}
                        · {clockTime(visit.visibleStartsAt, timezone)} –{' '}
                        {visit.uncertain
                          ? 'End unknown · shaded uncertainty'
                          : clockTime(visit.visibleEndsAt, timezone)}
                      </li>
                    ))}
                    {off.map((span, index) => (
                      <li key={index}>
                        <span className="font-medium">Time off: </span>
                        {intervalText(span.startsAt, span.endsAt, timezone)}
                      </li>
                    ))}
                    {!visits.length && !off.length ? (
                      <li className="text-muted-foreground">
                        No recorded visits or time off in this day. Check a candidate
                        window before proposing it.
                      </li>
                    ) : null}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>
      ))}
      {crew.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No eligible crew members match this calendar.
        </p>
      ) : null}
    </div>
  );
}

export function CrewAvailabilityTimeline({ initialDay }: { initialDay?: string }) {
  const trpc = useTRPC();
  const clock = useOrgClock();
  const [view, setView] = useState('day');
  const [selectedDay, setDay] = useState(initialDay ?? '');
  useEffect(() => {
    if (initialDay) setDay(initialDay);
  }, [initialDay]);
  const day = selectedDay || (clock.tz ? localDateOf(new Date(), clock.tz) : '');
  const days = day
    ? weekDays(view === 'week' ? weekStart(day) : day, view === 'week' ? 7 : 1)
    : [];
  const input =
    clock.tz && days[0]
      ? {
          startsAt: localWallTimeUtc(days[0], 0, clock.tz),
          endsAt: localWallTimeUtc(addDaysISO(days.at(-1)!, 1), 0, clock.tz),
        }
      : { startsAt: new Date(0), endsAt: new Date(1) };
  const q = useQuery({
    ...trpc.scheduleAvailability.timeline.queryOptions(input),
    enabled: Boolean(clock.tz && days.length),
  });
  const state = shownState(q, q.data);
  // The containing drawer presents the clock's loading/error/offline state and retry.
  if (clock.state !== 'ready') return null;
  const content = (
    <>
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          what="the crew calendar"
          icon={IconTeam}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data ? (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Calendar dates: {q.data.timezone} · Working-hours timezone:{' '}
            {q.data.workingHoursTimezone}. Read at{' '}
            {clockTime(q.data.asOf, q.data.timezone)}. Travel buffer:{' '}
            {q.data.travelBufferMinutes} minutes once between adjacent visits; not added
            to both bars.
          </p>
          <p className="text-xs text-muted-foreground">
            Green: working hours · Blue: booked visit · Red: time off · Dashed amber:
            unknown end. Blank space is not an availability verdict.
          </p>
          {!q.data.configured ? (
            <Notice tone="warn">Working hours are not fully configured.</Notice>
          ) : null}
          <CrewTimelineRows crew={q.data.crew} days={days} timezone={q.data.timezone} />
        </div>
      ) : null}
    </>
  );
  return (
    <section className="space-y-4" aria-label="Crew calendar">
      <h3 className="text-sm font-semibold">Crew calendar</h3>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Calendar date">
          <input
            type="date"
            className={field}
            value={day}
            disabled={!clock.tz}
            onChange={(event) => setDay(event.target.value)}
          />
        </Field>
        <Button
          variant="ghost"
          size="sm"
          disabled={!day}
          onClick={() => setDay(addDaysISO(day, view === 'week' ? -7 : -1))}
        >
          Previous {view}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!day}
          onClick={() => setDay(addDaysISO(day, view === 'week' ? 7 : 1))}
        >
          Next {view}
        </Button>
      </div>
      <UnderlineTabs value={view} onValueChange={setView}>
        <UnderlineTabStrip
          tabs={[
            { key: 'day', label: 'Day' },
            { key: 'week', label: 'Week' },
          ]}
        />
        <UnderlineTabPanel value="day">
          {view === 'day' ? content : null}
        </UnderlineTabPanel>
        <UnderlineTabPanel value="week">
          {view === 'week' ? content : null}
        </UnderlineTabPanel>
      </UnderlineTabs>
    </section>
  );
}
